import { Injectable } from '@angular/core';
import { CameraStatus } from './camera-status.service';

export interface FilterableDriver {
  partnerId: number;
  cameraStatus?: CameraStatus | null;
}

export interface CameraFilterOption {
  name: string;
  code: string;
  selected: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CameraFilterUtils {
  
  /**
   * Default camera filter options - reusable across components
   */
  static readonly DEFAULT_FILTER_OPTIONS: CameraFilterOption[] = [
    { name: 'All', code: 'all', selected: true },
    { name: 'Linked', code: 'linked', selected: false },
    { name: 'Inactive', code: 'inactive', selected: false },
    { name: 'None', code: 'none', selected: false },
  ];

  /**
   * Check if a driver matches the given filter criteria
   */
  static driverMatchesFilter<T extends FilterableDriver>(driver: T, filterCode: string): boolean {
    switch(filterCode) {
      case 'all':
        return true;
      case 'linked':
        return !!(driver.cameraStatus?.camera_id && driver.cameraStatus?.camera_online_status);
      case 'inactive':
        return !!(driver.cameraStatus?.camera_id && !driver.cameraStatus?.camera_online_status);
      case 'none':
        return !driver.cameraStatus?.camera_id;
      default:
        return true;
    }
  }

  /**
   * Apply filter to array of drivers
   */
  static filterDrivers<T extends FilterableDriver>(drivers: T[], filterCode: string): T[] {
    if (filterCode === 'all') {
      return [...drivers];
    }
    return drivers.filter(driver => this.driverMatchesFilter(driver, filterCode));
  }

  /**
   * Update filter selection state
   */
  static updateFilterSelection(filterOptions: CameraFilterOption[], selectedCode: string): CameraFilterOption[] {
    return filterOptions.map(option => ({
      ...option,
      selected: option.code === selectedCode
    }));
  }

  /**
   * Optimized incremental filter update - O(1) average case
   * Use this instead of re-filtering entire array when camera status changes
   */
  static updateFilteredDriversIncrementally<T extends FilterableDriver>(
    currentFilteredDrivers: T[],
    oldDriver: T,
    updatedDriver: T,
    currentFilterCode: string,
    allDrivers: T[]
  ): T[] {
    if (currentFilterCode === 'all') {
      // For 'all' filter, just return updated allDrivers array
      return [...allDrivers];
    }

    const oldMatched = this.driverMatchesFilter(oldDriver, currentFilterCode);
    const newMatched = this.driverMatchesFilter(updatedDriver, currentFilterCode);
    const filteredList = [...currentFilteredDrivers];
    const filteredIdx = filteredList.findIndex(d => d.partnerId === updatedDriver.partnerId);

    if (oldMatched && newMatched) {
      // Driver was in filter and still matches - update in place
      if (filteredIdx !== -1) {
        filteredList[filteredIdx] = updatedDriver;
      }
    } else if (!oldMatched && newMatched) {
      // Driver wasn't in filter but now matches - add it
      filteredList.push(updatedDriver);
    } else if (oldMatched && !newMatched) {
      // Driver was in filter but no longer matches - remove it
      if (filteredIdx !== -1) {
        filteredList.splice(filteredIdx, 1);
      }
    }
    // If !oldMatched && !newMatched, no change needed

    return filteredList;
  }
}